import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import queryString from "query-string";
import { recordSplitTestExposure } from "../services/api";

export const DEFAULT_FORM_COLORS = {
  submitButtonBackground: "#e15d3a",
  formHeaderBackground: "#198754"
};

/**
 * Split test on the order form (Cockpit, Asana 1219217770567617 / v2
 * 1219246123347655). Returns the text elements with an assigned text variant
 * applied, the effective colours and trust elements (form context plus an
 * assigned colour / feature variant) and a function that records the
 * exposure once per page life and visitor.
 */
export const applySplitTest = (textElements, splitTest) => {
  if (!textElements || !splitTest?.element) return textElements || {};
  if (splitTest.elementType && splitTest.elementType !== "text") {
    return textElements;
  }
  return { ...textElements, [splitTest.element]: splitTest.value };
};

export const resolveFormPresentation = ({
  textElements,
  formContext,
  splitTest
}) => {
  const colours = {
    submitButtonBackground:
      textElements?.submitButtonBackground ||
      formContext?.colours?.submitButtonBackground ||
      DEFAULT_FORM_COLORS.submitButtonBackground,
    formHeaderBackground:
      textElements?.formHeaderBackground ||
      formContext?.colours?.formHeaderBackground ||
      DEFAULT_FORM_COLORS.formHeaderBackground
  };
  const socialProof = formContext?.socialProof
    ? { ...formContext.socialProof }
    : null;
  const googleRating = formContext?.googleRating
    ? { ...formContext.googleRating }
    : null;
  if (splitTest?.elementType === "color" && splitTest.value) {
    colours[splitTest.element] = splitTest.value;
  }
  if (splitTest?.elementType === "feature") {
    const enabled = Boolean(splitTest.value?.enabled);
    if (splitTest.element === "socialProof" && socialProof) {
      socialProof.enabled = enabled;
      if (splitTest.value?.template) {
        socialProof.label = splitTest.value.template
          .replace(
            /\{\{\s*count\s*\}\}/g,
            new Intl.NumberFormat("de-DE").format(socialProof.count)
          )
          .replace(
            /\{\{\s*category\s*\}\}/g,
            socialProof.categoryName || "Ihr Projekt"
          );
      }
    }
    if (splitTest.element === "googleRating" && googleRating) {
      googleRating.enabled = enabled;
    }
  }
  return { colours, socialProof, googleRating };
};

export const useSplitTest = (baseTextElements) => {
  const { funnel, splitTest, formContext } = useSelector(
    (state) => state.funnelInfo
  );
  const textElements = useMemo(
    () => applySplitTest(baseTextElements, splitTest),
    [baseTextElements, splitTest]
  );
  const presentation = useMemo(
    () =>
      resolveFormPresentation({
        textElements: baseTextElements,
        formContext,
        splitTest
      }),
    [baseTextElements, formContext, splitTest]
  );

  const recordExposure = useCallback(() => {
    if (!splitTest?.experimentId) return;
    const isTestMode =
      queryString.parse(window.location.search).testMode === "true";
    if (isTestMode) return;
    const senderId = localStorage.getItem("senderId");
    if (!senderId) return;
    const guardKey = `splitTestExposed:${splitTest.experimentId}`;
    try {
      if (sessionStorage.getItem(guardKey)) return;
      sessionStorage.setItem(guardKey, "1");
    } catch {
      // storage unavailable: the backend deduplicates per visitor anyway
    }
    recordSplitTestExposure({
      experimentId: splitTest.experimentId,
      senderId,
      variant: splitTest.variant,
      funnelId: funnel?._id
    }).catch(() => {
      console.log("Error in recording split test exposure");
    });
  }, [splitTest, funnel?._id]);

  return { textElements, splitTest, presentation, recordExposure };
};
